import type { ShiftProductionFormPayload } from "../../../shared/shift-production-form/contracts";
import {
  numberOrNull,
  type Measurement,
  type ShiftFormValidationIssue,
  validationMessage,
} from "./form-state";
import {
  measurementUnit,
  withMeasurementValue,
} from "./measurement-form-state";

const labels: Record<Measurement["key"], string> = {
  THEORETICAL_LINE_SPEED: "Teorik Makina Hat Hızı / Adet",
  ACTUAL_LINE_SPEED: "Makina Hat Hızı / Adet",
  MACHINE_TEMPERATURE: "Makina Isı Derecesi",
  OVEN_TEMPERATURE: "Fırın Sıcaklığı",
  MOLD_TEMPERATURE: "Kalıp Sıcaklığı",
  TUNNEL_TEMPERATURE: "Tünel Sıcaklığı",
  UNIT_WEIGHT: "Ürün Ağırlığı",
  PRODUCT_MOISTURE: "Ürün Nem",
  AMBIENT_TEMPERATURE: "Ortam Sıcaklık",
  AMBIENT_HUMIDITY: "Ortam Nem %",
};

export function ShiftFormMeasurements({
  measurements,
  onChange,
  speedUnit = null,
  speedUnitHint = "Önce hat seçin",
  validationIssues,
  validationPrefix,
}: {
  measurements: ShiftProductionFormPayload["products"][number]["measurements"];
  onChange: (measurement: Measurement) => void;
  speedUnit?: string | null;
  speedUnitHint?: string;
  validationIssues: readonly ShiftFormValidationIssue[];
  validationPrefix: string;
}) {
  const actualMeasurements = measurements.filter(
    (item) =>
      item.key !== "THEORETICAL_LINE_SPEED" &&
      item.key !== "MACHINE_TEMPERATURE" &&
      !isTemperatureMeasurement(item.key),
  );
  const temperatures = measurements.filter((item) => isTemperatureMeasurement(item.key));
  const field = (measurement: Measurement) => {
    const unit = measurementUnit(measurement.key, speedUnit);
    const error = validationMessage(validationIssues, `${validationPrefix}.measurements.${measurement.key}`);
    return (
      <div className="shift-measurement-field">
        <div className="shift-measurement-number">
          <input
            data-shift-validation={`${validationPrefix}.measurements.${measurement.key}`}
            data-shift-diff-path={`${validationPrefix}.measurements.${measurement.key}`}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? `shift-error-${validationPrefix}-${measurement.key}` : undefined}
            aria-label={labels[measurement.key]}
            placeholder="Değer yazın"
            type="number"
            inputMode="decimal"
            step="any"
            min={
              ["MACHINE_TEMPERATURE", "OVEN_TEMPERATURE", "MOLD_TEMPERATURE", "TUNNEL_TEMPERATURE", "AMBIENT_TEMPERATURE"].includes(
                measurement.key,
              )
                ? undefined
                : 0
            }
            disabled={!unit}
            value={measurement.value ?? ""}
            onChange={(event) =>
              onChange({
                ...withMeasurementValue(
                  measurement,
                  numberOrNull(event.target.value),
                  speedUnit,
                ),
                measuredAt: null,
                pointOrMethod: null,
              })
            }
          />
          <span>{measurement.unit ?? unit ?? speedUnitHint}</span>
        </div>
        {error ? <small id={`shift-error-${validationPrefix}-${measurement.key}`} className="shift-field-error" role="alert">{error}</small> : null}
      </div>
    );
  };
  return (
    <div className="shift-measurement-table" data-shift-diff-path={`${validationPrefix}.measurements`}>
      <table className="shift-actual-parameters">
        <thead>
          <tr>
            {actualMeasurements.map((measurement) => (
              <th key={measurement.key} scope="col">
                {labels[measurement.key]} {isRequiredMeasurement(measurement.key) ? <RequiredMark /> : null}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            {actualMeasurements.map((measurement) => (
              <td key={measurement.key} data-label={labels[measurement.key]}>{field(measurement)}</td>
            ))}
          </tr>
        </tbody>
      </table>
      <div className="shift-paper-heading">Makine Isı Dereceleri</div>
      <table className="shift-actual-parameters shift-temperature-parameters">
        <thead>
          <tr>
            {temperatures.map((measurement) => (
              <th key={measurement.key} scope="col">{labels[measurement.key]}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            {temperatures.map((measurement) => (
              <td key={measurement.key} data-label={labels[measurement.key]}>{field(measurement)}</td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function isTemperatureMeasurement(key: Measurement["key"]) {
  return ["OVEN_TEMPERATURE", "MOLD_TEMPERATURE", "TUNNEL_TEMPERATURE"].includes(key);
}

function isRequiredMeasurement(key: Measurement["key"]) {
  return ["ACTUAL_LINE_SPEED", "UNIT_WEIGHT", "PRODUCT_MOISTURE", "AMBIENT_TEMPERATURE", "AMBIENT_HUMIDITY"].includes(key);
}

function RequiredMark() {
  return <span className="shift-required-mark" aria-label="zorunlu">*</span>;
}
